// Practice sets v4 (2026-10-08). Client-side mirror of the create-time rules in
// the shared backend module homework-v4.mjs (normalizeHomeworkV4): same limits,
// same messages, so the UI blocks exactly what claude-teacher-api would reject.
// The server stays the authority; this only gives instant feedback.
//
// Vocabulary: field (math|reading) > domain > topic (34 math families /
// 19 reading concepts; what teachers pick) > band skill (one topic at one
// level; where questions live). Level 1-7 is difficulty, not a tier.
// subject 'both' (2026-10-09): one set can mix Math and Reading & Writing topics
// (the whole SAT, or topics chosen from both). Units stay one subject.
import catalog from './homeworkTopicsV4.json';

export const CATALOG = catalog;
// Builder limits (2026-10-20, plan P7; BBO-89, BBO-95): 1 or 2 topics when the
// bank can fill them (topicPool), up to 20 questions. The server says what it
// takes (getHomeworkCapabilities.limits, limitsOf below); a backend that says
// nothing (teacher-api prod v41 and older) keeps 3 topics / 15 questions, so
// the UI never offers what that backend refuses. The screens pass the limits
// they got; V4_LIMITS is this build's own (and the tutor prompt's) rule.
export const V4_LIMITS = Object.freeze({ minCount: 4, maxCount: 20, minTopics: 1, minLevel: 1, maxLevel: 7, perTopic: 2, titleMax: 80 });
export const LEGACY_V4_LIMITS = Object.freeze({ ...V4_LIMITS, maxCount: 15, minTopics: 3 });
export function limitsOf(caps) {
  const l = caps && caps.limits;
  if (!l || !Number.isInteger(l.maxCount) || !Number.isInteger(l.minTopics)) return LEGACY_V4_LIMITS;
  // Never wider than this build knows.
  return Object.freeze({ ...V4_LIMITS, maxCount: Math.min(l.maxCount, V4_LIMITS.maxCount), minTopics: Math.max(l.minTopics, V4_LIMITS.minTopics) });
}
export const LEVELS = [1, 2, 3, 4, 5, 6, 7];
export const countOptions = (limits = V4_LIMITS) => Array.from({ length: limits.maxCount - limits.minCount + 1 }, (_, i) => limits.minCount + i);
export const COUNT_OPTIONS = countOptions();
const COVERAGES = ['field', 'domain', 'topics'];
export const SUBJECTS = ['math', 'reading', 'both'];
const LEVEL_MODES = ['student', 'range'];
const DOMAIN_LABELS = {
  algebra: 'Algebra',
  'advanced-math': 'Advanced Math',
  'problem-solving-and-data-analysis': 'Problem Solving & Data Analysis',
  'geometry-and-trigonometry': 'Geometry & Trigonometry',
  'information-and-ideas': 'Information & Ideas',
  'craft-and-structure': 'Craft & Structure',
  'expression-of-ideas': 'Expression of Ideas',
  'standard-english-conventions': 'Standard English Conventions',
};

const fail = (message, extra) => { const e = new Error(message); e.statusCode = 400; if (extra) e.details = extra; throw e; };

export const subjectLabel = subject => (subject === 'math' ? 'Math' : subject === 'both' ? 'Math + Reading & Writing' : 'Reading & Writing');
export const domainLabel = id => DOMAIN_LABELS[id] || id || '';
// Domains in College Board order (the catalog's weight table keeps that order).
const weightsOf = subject => (subject === 'both' ? { ...catalog.domainWeights.math, ...catalog.domainWeights.reading } : catalog.domainWeights[subject] || {});
export const domainsOf = subject => Object.keys(weightsOf(subject)).map(id => ({ id, label: domainLabel(id) }));
export const topicsOf = subject => (subject === 'both' ? [...(catalog.math || []), ...(catalog.reading || [])] : catalog[subject] || []);
// The subject a topic belongs to ('math' | 'reading'); topic ids never collide across the two.
export const subjectOfTopic = id => ((catalog.math || []).some(t => t.id === id) ? 'math' : (catalog.reading || []).some(t => t.id === id) ? 'reading' : null);
export const topicById = (subject, id) => topicsOf(subject).find(t => t.id === id) || null;
export const levelsOfTopic = topic => Object.keys(topic?.levels || {}).map(Number).sort((a, b) => a - b);
export const levelsInRange = (topic, min, max) => levelsOfTopic(topic).filter(l => l >= min && l <= max);
export const rangeLabel = (a, b) => (a === b ? `Level ${a}` : `Levels ${a}-${b}`);
const questionsAt = (topic, levels) => levels.reduce((s, l) => s + (topic.levels[l] || []).reduce((a, b) => a + b.n, 0), 0);

// "2, 4-7": consecutive levels collapse into a run.
export function compressLevels(levels) {
  const runs = [];
  for (const l of levels) {
    const last = runs[runs.length - 1];
    if (last && last[1] === l - 1) last[1] = l; else runs.push([l, l]);
  }
  return runs.map(([a, b]) => (a === b ? `${a}` : `${a}-${b}`)).join(', ');
}
// Chip text: the levels a topic actually has ("L2, 4-7").
export const topicLevelsLabel = topic => `L${compressLevels(levelsOfTopic(topic))}`;
// Why a chip is disabled under the teacher's range ("only Level 7"), or null when usable.
export function outOfRangeReason(topic, min, max) {
  if (levelsInRange(topic, min, max).length) return null;
  const ls = levelsOfTopic(topic);
  return ls.length === 1 ? `only Level ${ls[0]}` : `only Levels ${compressLevels(ls)}`;
}

export function coverageTopics(sel) {
  const all = topicsOf(sel.subject);
  if (sel.coverage === 'field') return all;
  if (sel.coverage === 'domain') return all.filter(t => t.domain === sel.domainId);
  return (sel.topicIds || []).map(id => all.find(t => t.id === id)).filter(Boolean);
}

// Buffy and older drafts speak in legacy question-concept ids; the server
// never sees those for v4. Topic ids pass through unchanged. Deduped, in order.
export function topicsFromLegacy(subject, ids) {
  const map = subject === 'both' ? { ...(catalog.legacyToTopic.math || {}), ...(catalog.legacyToTopic.reading || {}) } : catalog.legacyToTopic[subject] || {};
  return [...new Set((ids || []).map(id => map[id] || (topicById(subject, id) ? id : null)).filter(Boolean))];
}
// The reverse, one legacy id per topic, for Buffy's concept-numbered catalog.
// `usable` narrows to ids the legacy catalog still lists.
export function legacyIdsForTopics(subject, topicIds, usable = () => true) {
  const map = subject === 'both' ? { ...(catalog.legacyToTopic.math || {}), ...(catalog.legacyToTopic.reading || {}) } : catalog.legacyToTopic[subject] || {};
  const keys = Object.keys(map);
  return (topicIds || []).map(topic => keys.find(k => map[k] === topic && usable(k))).filter(Boolean);
}

// The questions one topic can give each student: at the teacher's levels
// (range), or at the topic's smallest level, since each student's own level
// can land on any level the topic has. Same as the server's topicPool.
export function topicPool(topic, levelMode, levelMin, levelMax) {
  if (levelMode === 'range') return { available: questionsAt(topic, levelsInRange(topic, levelMin, levelMax)), where: rangeLabel(levelMin, levelMax) };
  const low = levelsOfTopic(topic).map(l => ({ l, n: questionsAt(topic, [l]) })).reduce((a, b) => (!a || b.n < a.n ? b : a), null);
  return low ? { available: low.n, where: `Level ${low.l}` } : { available: 0, where: 'any level' };
}

// ---------- create-time normalize + validate (mirror of the server) ----------
// Returns the selection fields the HW# row will carry plus non-blocking warnings.
// `limits`: what the backend takes (limitsOf); the server's own rule by default.
export function normalizeHomeworkV4(input, limits = V4_LIMITS) {
  const subject = input.subject;
  if (!SUBJECTS.includes(subject)) fail('subject must be math, reading or both');
  const coverage = input.coverage;
  if (!COVERAGES.includes(coverage)) fail('coverage must be field, domain or topics');
  if (subject === 'both' && coverage === 'domain') fail('A Math + English set covers the whole SAT or topics you choose. Pick Math or English for one domain.');
  const domains = Object.keys(weightsOf(subject));
  let domainId = null;
  let topicIds = null;
  if (coverage === 'domain') {
    domainId = input.domainId;
    if (!domains.includes(domainId)) fail('unknown domain for this subject');
  }
  if (coverage === 'topics') {
    if (!Array.isArray(input.topicIds)) fail('topicIds required');
    topicIds = [...new Set(input.topicIds.filter(x => typeof x === 'string' && x))];
    if (topicIds.length < limits.minTopics) fail(`choose at least ${limits.minTopics} topic${limits.minTopics === 1 ? '' : 's'}`);
    const unknown = topicIds.filter(id => !topicById(subject, id));
    if (unknown.length) fail('one or more topics are not in this subject', { unknown });
  }
  const count = Number(input.count);
  if (!Number.isInteger(count) || count < limits.minCount || count > limits.maxCount) fail(`count must be ${limits.minCount}-${limits.maxCount}`);
  const levelMode = input.levelMode || 'student';
  if (!LEVEL_MODES.includes(levelMode)) fail('levelMode must be student or range');
  let levelMin = null;
  let levelMax = null;
  if (levelMode === 'range') {
    levelMin = Number(input.levelMin);
    levelMax = Number(input.levelMax);
    if (![levelMin, levelMax].every(l => Number.isInteger(l) && l >= limits.minLevel && l <= limits.maxLevel) || levelMin > levelMax) {
      fail('level range must be two levels from 1 to 7, low to high');
    }
  }
  const sel = { selectionVersion: 4, subject, coverage, domainId, topicIds, levelMode, levelMin, levelMax, count };
  const topics = coverageTopics(sel);
  const warnings = [];
  if (levelMode === 'range') {
    const inRange = topics.filter(t => levelsInRange(t, levelMin, levelMax).length);
    if (coverage === 'topics') {
      const blocked = topics.filter(t => !inRange.includes(t));
      if (blocked.length) {
        fail(`${blocked.map(t => t.label).join(', ')} ${blocked.length === 1 ? 'has' : 'have'} no questions at ${rangeLabel(levelMin, levelMax)}. Change the levels or remove ${blocked.length === 1 ? 'it' : 'them'}.`,
          { blockedTopicIds: blocked.map(t => t.id) });
      }
    }
    if (!inRange.length) fail(`No topic here has questions at ${rangeLabel(levelMin, levelMax)}.`);
    const available = inRange.reduce((s, t) => s + questionsAt(t, levelsInRange(t, levelMin, levelMax)), 0);
    if (available < count) fail(`Only ${available} questions exist at ${rangeLabel(levelMin, levelMax)} here. Choose fewer questions or widen the levels.`);
    if (coverage !== 'topics' && inRange.length <= 2) {
      warnings.push(`Only ${inRange.length} topic${inRange.length === 1 ? ' has' : 's have'} ${rangeLabel(levelMin, levelMax)} here: ${inRange.map(t => t.label).join(', ')}.`);
    }
  }
  // Few topics (plan P7, same as the server): with fewer chosen topics than
  // ceil(count / perTopic), each student's set splits the count evenly, so
  // each topic must hold ceil(count / topics) questions where students land.
  if (coverage === 'topics' && topics.length && topics.length < Math.ceil(count / limits.perTopic)) {
    const need = Math.ceil(count / topics.length);
    const pools = topics.map(t => ({ t, ...topicPool(t, levelMode, levelMin, levelMax) }));
    const short = pools.find(p => p.available < need);
    if (short) {
      const maxCount = Math.min(limits.maxCount, Math.min(...pools.map(p => p.available)) * topics.length);
      const fewer = maxCount >= limits.minCount ? ` or ${maxCount} or fewer questions` : '';
      fail(`The bank has only ${short.available} questions on ${short.t.label} at ${short.where}. Choose ${topics.length === 1 ? 'a second topic' : 'another topic'}${fewer}.`,
        { topicId: short.t.id, available: short.available, maxCount });
    }
  }
  sel.domainIds = [...new Set(topics.map(t => t.domain))];
  return { selection: sel, warnings };
}

// Non-throwing form for render-time checks: { ok, error, details, warnings, selection }.
export function checkHomeworkV4(input, limits = V4_LIMITS) {
  try {
    const { selection, warnings } = normalizeHomeworkV4(input, limits);
    return { ok: true, error: '', details: null, warnings, selection };
  } catch (err) {
    return { ok: false, error: err.message, details: err.details || null, warnings: [], selection: null };
  }
}

// Short reading of what a set covers ("Whole Math", "Algebra", "A, B, C"),
// worded like the picker's whole-field card.
export function coverageSummary({ subject, coverage, domainId, topicIds }) {
  if (coverage === 'domain') return domainLabel(domainId) || 'One domain';
  if (coverage === 'topics') {
    const labels = (topicIds || []).map(id => topicById(subject, id)?.label).filter(Boolean);
    return labels.length ? labels.join(', ') : 'Chosen topics';
  }
  return subject === 'both' ? 'Whole SAT' : `Whole ${subjectLabel(subject)}`;
}
export const levelsSummary = ({ levelMode, levelMin, levelMax }) => (levelMode === 'range' ? rangeLabel(levelMin, levelMax) : 'Each student’s level');

// Default title for a fresh draft, within the server's 80-character cap. It
// names every topic up to three, then says "& more" (audit W13: a third topic
// used to drop out of the name without a word). Long topic names fall back to
// fewer names plus "& more", then to the subject, never a cut word.
export function suggestedTitleV4({ subject, coverage, domainId, topicIds }) {
  const candidates = [];
  if (coverage === 'domain' && domainId) candidates.push(`${domainLabel(domainId)} practice`);
  if (coverage === 'topics') {
    const labels = (topicIds || []).map(id => topicById(subject, id)?.label).filter(Boolean);
    const tail = labels.length > 2 ? ' review' : ' practice';
    const named = (names, more) => (more ? `${names.join(', ')} & more`
      : names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}` : names[0]);
    for (let k = Math.min(3, labels.length); k >= 1; k -= 1) candidates.push(`${named(labels.slice(0, k), k < labels.length)}${tail}`);
  }
  candidates.push(subject === 'both' ? 'SAT practice' : `${subjectLabel(subject)} practice`);
  return candidates.find(name => name.length <= V4_LIMITS.titleMax);
}
